import { useState, type FormEvent, type ReactNode } from "react";
import { trackEvent } from "../../lib/analytics";
import { CONTACT_FORM_COPY, hasValidPhone } from "../../lib/contactForm";
import type { SupportedLocale } from "../../lib/localization";
import { submitPublicLead } from "../../lib/publicLead";
import { SOCIAL_ICONS } from "./SiteFooter";

const fieldCls =
  "w-full rounded-lg bg-[#2D3535] px-3 text-[16px] text-white placeholder:text-[#999] focus:outline-none focus:ring-1 focus:ring-[var(--public-accent)]";

function Field({ label, children, wide }: { label: ReactNode; children: ReactNode; wide?: boolean }) {
  return (
    <label className={`flex flex-col gap-[7px] min-[810px]:gap-[10px] ${wide ? "col-span-2" : ""}`}>
      <span className="text-[13px] font-medium leading-[1.5] text-[#A3A3A3] min-[810px]:text-[15px]">{label}</span>
      {children}
    </label>
  );
}

function ContactForm({
  locale,
  serviceOptions,
  submitLabel,
}: {
  locale: SupportedLocale;
  serviceOptions: string[];
  submitLabel: string;
}) {
  const copy = CONTACT_FORM_COPY[locale];
  const tr = (en: string, es: string) => (locale === "es" ? es : en);
  const [status, setStatus] = useState<"idle" | "sending" | "done">("idle");
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const value = (key: string) => String(data.get(key) || "").trim();
    setError(null);
    if (value("phone") && !hasValidPhone(value("phone"))) {
      setError(copy.phoneInvalid);
      return;
    }
    setStatus("sending");
    try {
      await submitPublicLead({
        name: value("name"),
        email: value("email"),
        phone: value("phone"),
        company: value("company"),
        productInterest: value("service"),
        budget: value("budget"),
        message: value("message"),
        source: "website",
        landingUrl: window.location.href,
        locale,
      });
      trackEvent("website_quote_submit", { locale, product: value("service"), hasBudget: Boolean(value("budget")) });
      form.reset();
      setStatus("done");
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : copy.error);
      setStatus("idle");
    }
  };

  if (status === "done") {
    return <p className="py-16 text-center text-[16px] font-medium leading-[1.5] text-white">{copy.success}</p>;
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-x-[28px] gap-y-5 min-[810px]:gap-x-5">
      <Field label={tr("Name", "Nombre")} wide>
        <input name="name" required autoComplete="name" placeholder="Jane" className={`${fieldCls} h-[40px]`} />
      </Field>
      <Field label={tr("Email address", "Dirección de correo")}>
        <input name="email" type="email" required autoComplete="email" placeholder="jane@gmail.com" className={`${fieldCls} h-[40px]`} />
      </Field>
      <Field label={tr("Phone", "Teléfono")}>
        <input name="phone" type="tel" required autoComplete="tel" placeholder="(813) 123 4567" className={`${fieldCls} h-[40px]`} />
      </Field>
      <Field label={tr("Type of Service", "Tipo de Servicio")} wide>
        <select name="service" required defaultValue="" className={`${fieldCls} h-[40px] appearance-none`}>
          <option value="" disabled>
            {tr("Select…", "Select…")}
          </option>
          {serviceOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </Field>
      <Field label={tr("Business Name", "Nombre de Negocio")}>
        <input name="company" required autoComplete="organization" placeholder={tr("My Business", "Mi Negocio")} className={`${fieldCls} h-[40px]`} />
      </Field>
      <Field
        label={
          <>
            <span className="min-[810px]:hidden">{tr("Budget (USD)", "Presupuesto (USD)")}</span>
            <span className="hidden min-[810px]:inline">{tr("Estimated Budget (USD)", "Presupuesto Estimado (USD)")}</span>
          </>
        }
      >
        <input name="budget" type="number" min={0} placeholder="$0" className={`${fieldCls} h-[40px]`} />
      </Field>
      <Field label={tr("Message", "Mensaje")} wide>
        <textarea
          name="message"
          required
          rows={6}
          placeholder={tr("How can we help you?", "¿Cómo podemos ayudarte?")}
          className={`${fieldCls} h-[188px] resize-none py-2`}
        />
      </Field>
      {error ? <p className="col-span-2 text-sm text-red-300">{error}</p> : null}
      <button
        type="submit"
        disabled={status === "sending"}
        className="col-span-2 h-[40px] rounded-lg bg-[var(--public-accent)] text-[18px] font-semibold text-[var(--public-cta-ink)] transition-opacity hover:opacity-90 disabled:opacity-60"
      >
        {status === "sending" ? copy.sending : submitLabel}
      </button>
    </form>
  );
}

function InfoBlock({ title, text, email }: { title: string; text: string; email: string }) {
  const address = email.replace(/^mailto:/, "");
  return (
    <div className="flex flex-col gap-2 border-b border-white/10 pb-6 min-[810px]:gap-3 min-[810px]:pb-12">
      <h3 className="text-[16px] font-bold leading-[1.3] text-white min-[810px]:text-[20px]">{title}</h3>
      {text ? <p className="text-[13px] font-medium leading-[1.5] text-[#9F9F9F] min-[810px]:text-[16px]">{text}</p> : null}
      {address ? (
        <a href={`mailto:${address}`} className="w-fit text-[18px] font-semibold text-[var(--public-accent)] underline underline-offset-2">
          {address}
        </a>
      ) : null}
    </div>
  );
}

export function SiteContactPage({
  title,
  description,
  image,
  general,
  support,
  socialTitle,
  socials,
  serviceOptions,
  submitLabel,
  locale,
}: {
  title: string;
  description: string;
  image?: string;
  general: { title: string; text: string; email: string };
  support: { title: string; text: string; email: string };
  socialTitle: string;
  socials: Array<{ name: string; href: string }>;
  serviceOptions: string[];
  submitLabel: string;
  locale: SupportedLocale;
}) {
  return (
    <>
      <section id="hero" className="relative h-[394px] overflow-hidden min-[810px]:h-[400px]">
        {image ? <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" /> : null}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.7)_0%,var(--public-ink)_100%)]" />
        <div className="relative mx-auto flex max-w-[864px] flex-col items-center px-6 pt-[170px] text-center min-[810px]:pt-[210px]">
          <h1 className="site-h2 text-white">{title}</h1>
          {description ? <p className="site-lead mt-4 max-w-[740px] text-white/80">{description}</p> : null}
        </div>
      </section>

      <section id="contact" className="site-container grid gap-12 pt-10 min-[810px]:pt-20 min-[1200px]:grid-cols-[420px_700px] min-[1200px]:justify-between">
        <div className="flex flex-col gap-6 min-[810px]:gap-12 min-[1200px]:pt-2">
          <InfoBlock {...general} />
          <InfoBlock {...support} />
          <div className="flex flex-col gap-5">
            <h3 className="text-[16px] font-bold leading-[1.3] text-white min-[810px]:text-[20px]">{socialTitle}</h3>
            <div className="flex gap-4">
              {socials.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name}
                  className="text-[#A3A3A3] transition-colors hover:text-white"
                >
                  <svg viewBox="0 0 20 20" className="h-6 w-6" aria-hidden="true">
                    <path d={SOCIAL_ICONS[social.name]} fill="currentColor" />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-2xl bg-[#151E1E] px-4 py-6 min-[810px]:p-8">
          <ContactForm locale={locale} serviceOptions={serviceOptions} submitLabel={submitLabel} />
        </div>
      </section>
    </>
  );
}
